/**
 * Editor — monta a lista de compras (fiel ao HTML, com design system do ERP).
 * Edição inline de qt/nome/dimensões, ocultar do print, item manual (com opção
 * "Novo módulo"), e CONFERÊNCIA AO VIVO por item (check à esquerda → verde +
 * desabilita a edição daquele item + contador no rodapé). A conferência é
 * instantânea (estado no contexto persistido) e só vai pro backend ao
 * finalizar/salvar. "Gerar lista de compras" abre o preview de impressão.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowLeft, FiCheck, FiEye, FiEyeOff, FiMoreVertical, FiPackage, FiPlus,
  FiPrinter, FiTrash2,
} from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import EntityHeader from "../../components/EntityHeader";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { criarLista } from "../../services/revisorFerragens";
import { arredQt, groupByModule } from "./lib/parseProjeto";
import { moduloSemCodigo, montarPayloadLista, resolverModuloManual } from "./lib/documento";
import ManualForm, { formManualInicial } from "./ManualForm";
import { descartarAmbientesPersistidos, useRevisor } from "./context";
import {
  AmbienteCard, AmbienteFooter, AmbienteHeader, AmbienteSub, AmbienteTitle,
  ColNum, ConferCheck, DimCell, EmptyBox, ItemNota, ItemRow, ModuleCount,
  ModuleHeader, ModuleMid, ModuleName, Page, RowMenuBtn, ShowHidden,
  SmallInput, StickyActions,
} from "./styles";

const EDITOR_COLS = "30px 40px 64px 1fr 120px 32px";

function statusLabel(s) {
  const map = { production: "Produção", producao: "Produção", draft: "Rascunho",
    rascunho: "Rascunho", fechado: "Fechado", closed: "Fechado" };
  return map[(s || "").toLowerCase()] || s || "—";
}

function RowMenu({ item, disabled, onToggleHidden, onRemove }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative", justifySelf: "end" }}>
      <RowMenuBtn type="button" disabled={disabled} onClick={() => setOpen((v) => !v)} aria-label="Ações do item">
        <FiMoreVertical size={15} />
      </RowMenuBtn>
      {open ? (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 30 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: "absolute", right: 0, top: 32, zIndex: 31, minWidth: 170,
              background: "var(--surface)", border: "1px solid var(--border)",
              borderRadius: 10, boxShadow: "0 12px 28px rgba(0,0,0,.18)", padding: 4,
            }}
          >
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              style={{ width: "100%", justifyContent: "flex-start", border: "none", background: "transparent" }}
              onClick={() => { onToggleHidden(); setOpen(false); }}
            >
              {item.hidden ? <FiEye size={14} /> : <FiEyeOff size={14} />}
              {item.hidden ? "Mostrar no print" : "Ocultar do print"}
            </button>
            {item.manual ? (
              <button
                type="button"
                className="btn btn--sm"
                style={{ width: "100%", justifyContent: "flex-start", border: "none", background: "transparent", color: "var(--danger-text)" }}
                onClick={() => { onRemove(); setOpen(false); }}
              >
                <FiTrash2 size={14} /> Remover item
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default function Editor() {
  const { request } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  // Opera direto sobre o contexto (persistido) → edições/conferência sobrevivem
  // ao remount que o App.jsx provoca a cada navegação.
  const { ambientes, setAmbientes } = useRevisor();

  const [mostrarOcultos, setMostrarOcultos] = useState({});
  const [manualPara, setManualPara] = useState(null); // projectId do ambiente
  const [manualForm, setManualForm] = useState(null);

  function patchItem(projectId, itemId, patch) {
    setAmbientes((prev) =>
      prev.map((a) =>
        a.projectId !== projectId
          ? a
          : { ...a, items: a.items.map((it) => (it.id === itemId ? { ...it, ...patch } : it)) },
      ),
    );
  }

  function toggleConferido(projectId, item) {
    patchItem(projectId, item.id, { conferido: !item.conferido });
  }

  function removerItem(projectId, itemId) {
    setAmbientes((prev) =>
      prev.map((a) =>
        a.projectId !== projectId ? a : { ...a, items: a.items.filter((it) => it.id !== itemId) },
      ),
    );
  }

  function removerAmbiente(projectId) {
    setAmbientes((prev) => prev.filter((a) => a.projectId !== projectId));
  }

  function abrirManual(projectId) {
    setManualPara(projectId);
    setManualForm(formManualInicial());
  }

  function adicionarManual() {
    const amb = ambientes.find((a) => a.projectId === manualPara);
    if (!amb || !manualForm?.nome.trim()) {
      toast.error("Informe a descrição do item.");
      return;
    }
    const mod = resolverModuloManual(manualForm, groupByModule(amb.items));
    if (!mod) {
      toast.error("Digite o nome do novo módulo.");
      return;
    }
    const novo = {
      id: "manual_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6),
      key: "manual::" + Math.random(),
      moduleId: mod.moduleId, moduleName: mod.moduleName, moduleOrder: mod.moduleOrder,
      category: manualForm.categoria || "Outros",
      name: manualForm.nome.trim(),
      manufacturer: "",
      description: "",
      dimensions: manualForm.dimensoes.trim(),
      qt: arredQt(manualForm.quantidade) || 1,
      origin: "manual",
      hidden: false,
      manual: true,
      conferido: false,
    };
    setAmbientes((prev) =>
      prev.map((a) => (a.projectId === manualPara ? { ...a, items: [...a.items, novo] } : a)),
    );
    setManualPara(null);
    setManualForm(null);
    toast.success("Item adicionado.");
  }

  async function salvarNoHistorico() {
    if (!ambientes.length) return;
    const lista = await criarLista(request, montarPayloadLista(ambientes));
    descartarAmbientesPersistidos();
    navigate(`../historico/${lista.id}`);
  }

  if (!ambientes.length) {
    return (
      <Page>
        <EntityHeader title="Editor da lista" onBack={() => navigate("..")} backLabel="Voltar aos projetos" />
        <EmptyBox>
          <div
            style={{
              width: 64, height: 64, borderRadius: "50%", background: "var(--info-bg)",
              color: "var(--accent)", display: "grid", placeItems: "center", margin: "0 auto 16px",
            }}
          >
            <FiPackage size={28} />
          </div>
          <div style={{ fontWeight: 600, color: "var(--ink)", fontSize: 16, marginBottom: 6 }}>
            Nenhum ambiente carregado
          </div>
          <div style={{ maxWidth: 420, margin: "0 auto" }}>
            Use a seta <strong>“Voltar aos projetos”</strong> acima, selecione um ou mais
            ambientes e clique em <strong>“Carregar lista de compras”</strong> para montar a lista.
          </div>
        </EmptyBox>
      </Page>
    );
  }

  return (
    <Page>
      <EntityHeader
        title="Editor da lista"
        subtitle={`${ambientes.length} ${ambientes.length === 1 ? "ambiente carregado" : "ambientes carregados"} — confira os itens e gere a lista.`}
        onBack={() => navigate("..")}
        backLabel="Voltar aos projetos"
      />

      {ambientes.map((amb) => (
        <AmbienteEditor
          key={amb.projectId}
          amb={amb}
          mostrarOcultos={!!mostrarOcultos[amb.projectId]}
          onMostrarOcultos={() => setMostrarOcultos((m) => ({ ...m, [amb.projectId]: true }))}
          onPatch={(itemId, patch) => patchItem(amb.projectId, itemId, patch)}
          onConferir={(item) => toggleConferido(amb.projectId, item)}
          onRemoverItem={(itemId) => removerItem(amb.projectId, itemId)}
          onRemoverAmbiente={() => removerAmbiente(amb.projectId)}
          onAddManual={() => abrirManual(amb.projectId)}
        />
      ))}

      <StickyActions>
        <button className="btn btn--secondary" onClick={() => navigate("..")}>
          <FiArrowLeft size={14} /> Projetos
        </button>
        <AsyncButton variant="secondary" onClick={salvarNoHistorico} successMessage="Lista salva no histórico.">
          Salvar no histórico
        </AsyncButton>
        <button className="btn btn--primary" onClick={() => navigate("../imprimir")}>
          <FiPrinter size={14} /> Gerar lista de compras
        </button>
      </StickyActions>

      <SlideOverPanel
        open={!!manualPara}
        onClose={() => { setManualPara(null); setManualForm(null); }}
        title="Adicionar item manualmente"
        subtitle="Incluído apenas nesta lista. Não altera o projeto no DinaBox."
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn--secondary" onClick={() => { setManualPara(null); setManualForm(null); }}>
              Cancelar
            </button>
            <button className="btn btn--primary" onClick={adicionarManual}>Adicionar</button>
          </div>
        }
      >
        {manualForm ? (
          <ManualForm
            form={manualForm}
            setForm={setManualForm}
            modulos={
              manualPara
                ? groupByModule((ambientes.find((a) => a.projectId === manualPara) || {}).items || [])
                : []
            }
          />
        ) : null}
      </SlideOverPanel>
    </Page>
  );
}

function AmbienteEditor({
  amb, mostrarOcultos, onMostrarOcultos, onPatch, onConferir, onRemoverItem, onRemoverAmbiente, onAddManual,
}) {
  const visiveis = amb.items.filter((it) => mostrarOcultos || !it.hidden);
  const ocultos = amb.items.filter((it) => it.hidden);
  const grupos = groupByModule(visiveis);

  // Conferíveis = o que de fato vai pra impressão (não-oculto + qt > 0).
  const checaveis = amb.items.filter((i) => !i.hidden && Number(i.qt) > 0);
  const conferidos = checaveis.filter((i) => i.conferido).length;
  const tudoConferido = checaveis.length > 0 && conferidos === checaveis.length;

  let n = 0;

  return (
    <AmbienteCard>
      <AmbienteHeader>
        <div>
          <AmbienteTitle>{amb.raw?.project_description || "Ambiente"}</AmbienteTitle>
          <AmbienteSub>
            <span>#{amb.projectId}</span>
            <span>Cliente: {amb.raw?.project_customer_name || amb.raw?.project_client_name || "—"}</span>
            <span>Status: {statusLabel(amb.raw?.project_status)}</span>
          </AmbienteSub>
        </div>
        <button className="btn btn--secondary btn--sm" onClick={onRemoverAmbiente}>Remover</button>
      </AmbienteHeader>

      {grupos.map((g) => (
        <div key={g.moduleId}>
          <ModuleHeader>
            {!moduloSemCodigo(g.moduleId) ? <ModuleMid>{g.moduleId}</ModuleMid> : null}
            <ModuleName>{g.moduleName}</ModuleName>
            <ModuleCount>{g.items.length} {g.items.length === 1 ? "item" : "itens"}</ModuleCount>
          </ModuleHeader>
          {g.items.map((it) => {
            const conf = Boolean(it.conferido);
            const numero = it.hidden ? "—" : (n += 1);
            return (
              <ItemRow key={it.id} $cols={EDITOR_COLS} $manual={it.manual} $hidden={it.hidden} $confered={conf}>
                <ConferCheck
                  type="button"
                  $checked={conf}
                  title={conf ? "Desmarcar conferência" : "Marcar como conferido"}
                  aria-label={conf ? "Desmarcar conferência" : "Marcar como conferido"}
                  onClick={() => onConferir(it)}
                >
                  {conf ? <FiCheck size={13} /> : null}
                </ConferCheck>
                <ColNum>{numero}</ColNum>
                <SmallInput
                  $center
                  $confered={conf}
                  type="number"
                  min="0"
                  step="any"
                  disabled={conf}
                  aria-label={`Quantidade de ${it.name || "item"}`}
                  value={it.qt}
                  onChange={(e) => onPatch(it.id, { qt: arredQt(e.target.value) })}
                />
                <div>
                  <SmallInput
                    $confered={conf}
                    disabled={conf}
                    aria-label="Descrição do item"
                    value={it.name}
                    onChange={(e) => onPatch(it.id, { name: e.target.value })}
                  />
                  {it.manufacturer || it.description ? (
                    <ItemNota title={[it.manufacturer, it.description].filter(Boolean).join(" · ")}>
                      {[it.manufacturer, it.description].filter(Boolean).join(" · ")}
                    </ItemNota>
                  ) : null}
                </div>
                <DimCell>
                  <SmallInput
                    $center
                    $confered={conf}
                    disabled={conf}
                    placeholder="—"
                    aria-label="Dimensões do item"
                    value={it.dimensions}
                    onChange={(e) => onPatch(it.id, { dimensions: e.target.value })}
                  />
                </DimCell>
                <RowMenu
                  item={it}
                  disabled={conf}
                  onToggleHidden={() => onPatch(it.id, { hidden: !it.hidden })}
                  onRemove={() => onRemoverItem(it.id)}
                />
              </ItemRow>
            );
          })}
        </div>
      ))}

      {!mostrarOcultos && ocultos.length > 0 ? (
        <ShowHidden onClick={onMostrarOcultos}>
          Mostrar {ocultos.length} item(ns) ocultos por padrão (custos de execução)
        </ShowHidden>
      ) : null}

      <AmbienteFooter>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <span>{checaveis.length} de {amb.items.length} itens serão impressos</span>
          <span style={{ color: tudoConferido ? "var(--success-text)" : "var(--ink-3)", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 5 }}>
            {tudoConferido ? <FiCheck size={13} /> : null}
            {conferidos} de {checaveis.length} conferido{checaveis.length !== 1 ? "s" : ""}
          </span>
        </span>
        <button className="btn btn--secondary btn--sm" onClick={onAddManual}>
          <FiPlus size={13} /> Adicionar item manualmente
        </button>
      </AmbienteFooter>
    </AmbienteCard>
  );
}
